import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import { useNotification } from '../context/NotificationContext';
import { API_URL } from '../config/api';
import StripePayment, { PAYMENT_RETURN_KEY } from '../components/StripePayment';
import PreorderTimePicker from '../components/PreorderTimePicker';
import ItemDetailModal from '../components/ItemDetailModal';
import { isCurrentlyOpen } from '../utils/openingHours';
import './Cart.css';

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { currentAddress, setCurrentAddress } = useDeliveryAddress();
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart, removeRestaurantCart, updateItemVariations } = useCart();
  const { showError, showSuccess } = useNotification();
  const [restaurantData, setRestaurantData] = useState({}); // Store restaurant/store data with distance info
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [showPaymentForm, setShowPaymentForm] = useState({});
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmount, setWalletAmount] = useState(0);
  const [processingOrder, setProcessingOrder] = useState(null);
  const [processedPaymentIntents, setProcessedPaymentIntents] = useState(new Set());
  const [restaurantInstructions, setRestaurantInstructions] = useState('');
  const [driverInstructions, setDriverInstructions] = useState('');
  const [scheduledTimes, setScheduledTimes] = useState({}); // Store scheduled time per restaurant
  const [showPreorderPicker, setShowPreorderPicker] = useState({}); // Track which restaurant's picker is open
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const lastPrefilledUserIdRef = useRef(null);
  const processedPaymentIntentsRef = useRef(new Set()); // Sync guard so same payment intent never places two orders
  const [editingItem, setEditingItem] = useState(null); // Track which item is being edited
  const [showEditModal, setShowEditModal] = useState(false);

  const handleRemoveItem = (restaurantId, item) => {
    removeFromCart(restaurantId, item._id, item.selectedVariations || null, item.selectedIngredients || null);
  };

  const handleUpdateQuantity = (restaurantId, item, newQuantity) => {
    updateQuantity(restaurantId, item._id, newQuantity, item.selectedVariations || null, item.selectedIngredients || null);
  };

  const fetchWallet = async () => {
    try {
      const response = await axios.get(`${API_URL}/wallet/me`);
      setWallet(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchWallet();
      // Pre-fill customer details only when user first loads or switches account (do not overwrite edits when user ref updates)
      const userId = user._id?.toString?.() || user._id;
      if (userId && lastPrefilledUserIdRef.current !== userId) {
        lastPrefilledUserIdRef.current = userId;
        setCustomerDetails({
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          phone: user.phoneNumber || '',
          email: user.email || ''
        });
      }
    } else {
      lastPrefilledUserIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchRestaurantData = useCallback(async (id, type) => {
    try {
      if (!currentAddress?.coordinates?.lat || !currentAddress?.coordinates?.lng) {
        return;
      }
      const endpoint = type === 'store' ? `${API_URL}/stores/${id}` : `${API_URL}/restaurants/${id}`;
      const lat = parseFloat(currentAddress.coordinates.lat);
      const lng = parseFloat(currentAddress.coordinates.lng);
      
      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        console.warn('Invalid coordinates for restaurant data fetch:', { lat, lng });
        return;
      }
      
      const url = `${endpoint}?lat=${lat}&lng=${lng}`;
      const response = await axios.get(url);
      setRestaurantData(prev => ({
        ...prev,
        [id]: response.data
      }));
    } catch (error) {
      console.error('Error fetching restaurant/store data:', error);
    }
  }, [currentAddress]);

  useEffect(() => {
    // Fetch restaurant/store data to check delivery zones when address changes
    if (cart.length > 0 && currentAddress?.coordinates?.lat && currentAddress?.coordinates?.lng) {
      const lat = parseFloat(currentAddress.coordinates.lat);
      const lng = parseFloat(currentAddress.coordinates.lng);
      
      // Validate coordinates
      if (!isNaN(lat) && !isNaN(lng)) {
        // Force re-fetch for all restaurants in cart when address changes
        cart.forEach((restaurantCart) => {
          fetchRestaurantData(restaurantCart.restaurantId, restaurantCart.type);
        });
      }
    } else if (cart.length > 0 && (!currentAddress || !currentAddress.coordinates)) {
      // Clear restaurant data if no address is set
      setRestaurantData({});
    }
  }, [cart, currentAddress, fetchRestaurantData]);

  // When user selects a saved address with delivery instructions, pre-fill driver instructions in cart
  useEffect(() => {
    if (currentAddress?.deliveryInstructions != null && currentAddress.deliveryInstructions !== '') {
      setDriverInstructions(currentAddress.deliveryInstructions);
    }
  }, [currentAddress?.deliveryInstructions]);

  // Handle return from Stripe redirect (e.g. iDEAL, Bancontact) – complete order and clean URL
  useEffect(() => {
    const paymentReturn = searchParams.get('payment_return');
    const paymentIntentIdFromUrl = searchParams.get('payment_intent');
    const redirectStatus = searchParams.get('redirect_status');

    if (paymentReturn !== '1' || !paymentIntentIdFromUrl) return;

    const completeRedirectPayment = async () => {
      let stored;
      try {
        stored = sessionStorage.getItem(PAYMENT_RETURN_KEY);
      } catch {
        stored = null;
      }
      sessionStorage.removeItem(PAYMENT_RETURN_KEY);
      setSearchParams({}, { replace: true });

      if (redirectStatus !== 'succeeded') {
        showError(t('payment.redirectFailed') || 'Payment was not completed. Please try again.');
        return;
      }

      const parsed = stored ? (() => { try { return JSON.parse(stored); } catch { return null; } })() : null;
      const restaurantId = parsed?.restaurantId;
      const storedPaymentIntentId = parsed?.paymentIntentId;

      if (paymentIntentIdFromUrl !== storedPaymentIntentId || !restaurantId) {
        showError(t('payment.redirectInvalid') || 'Invalid payment return. Please try again.');
        return;
      }

      const restaurantCart = cart.find((rc) => rc.restaurantId === restaurantId);
      if (!restaurantCart || restaurantCart.items.length === 0) {
        showError(t('payment.redirectCartChanged') || 'Cart has changed. Please place your order again.');
        return;
      }

      try {
        const confirmRes = await axios.post(
          `${API_URL}/payments/confirm-payment`,
          { paymentIntentId: paymentIntentIdFromUrl },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (!confirmRes.data?.success) {
          showError(t('payment.error') || 'Payment could not be confirmed.');
          return;
        }
      } catch (err) {
        console.error('Confirm payment error:', err);
        showError(err.response?.data?.message || t('payment.error') || 'Payment could not be confirmed.');
        return;
      }

      if (processedPaymentIntentsRef.current.has(paymentIntentIdFromUrl)) return;
      processedPaymentIntentsRef.current.add(paymentIntentIdFromUrl);
      setProcessedPaymentIntents((prev) => new Set(prev).add(paymentIntentIdFromUrl));
      handlePlaceOrder(restaurantCart, paymentIntentIdFromUrl);
    };

    completeRedirectPayment();
  }, [searchParams, cart, setSearchParams, showError, t]);

  const handleApplyDiscountCode = async (restaurantCart) => {
    if (!discountCode.trim()) {
      setAppliedDiscount(null);
      return;
    }

    // Only one discount code can be active at a time
    if (appliedDiscount && appliedDiscount.restaurantId !== restaurantCart.restaurantId) {
      showError(t('cart.onlyOneDiscountAtATime') || 'Only one discount code can be active at a time. Remove the current discount first.');
      return;
    }

    try {
      const subtotalInclTax = restaurantCart.items.reduce((sum, item) => {
        let basePrice = item.price || 0;
        if (item.variations && item.selectedVariations) {
          item.variations.forEach(variation => {
            const selected = item.selectedVariations[variation.name];
            if (selected) {
              if (Array.isArray(selected)) {
                selected.forEach(optionName => {
                  const option = variation.options.find(opt => opt.name === optionName);
                  if (option) basePrice += option.price || 0;
                });
              } else {
                const option = variation.options.find(opt => opt.name === selected);
                if (option) basePrice += option.price || 0;
              }
            }
          });
        }
        return sum + (basePrice * item.quantity);
      }, 0);

      const restaurantInfo = restaurantData[restaurantCart.restaurantId];
      const deliveryFee = deliveryMethod === 'delivery' 
        ? (restaurantInfo?.deliveryFee ?? restaurantCart.deliveryFee ?? 0) 
        : 0;
      const serviceFeeRate = 0;
      const serviceFee = (subtotalInclTax + deliveryFee) * (serviceFeeRate / 100);
      const orderAmount = subtotalInclTax + deliveryFee + serviceFee;

      const response = await axios.post(`${API_URL}/discount-codes/validate`, {
        code: discountCode.trim(),
        restaurantId: restaurantCart.restaurantId,
        restaurantType: restaurantCart.type === 'store' ? 'Store' : 'Restaurant',
        orderAmount: orderAmount
      });

      // Store restaurant ID with discount for validation
      setAppliedDiscount({
        ...response.data.discountCode,
        restaurantId: restaurantCart.restaurantId
      });
    } catch (error) {
      showError(error.response?.data?.message || 'Invalid discount code');
      setAppliedDiscount(null);
    }
  };

  const handlePlaceOrder = async (restaurantCart, paymentIntentId = null) => {
    if (!restaurantCart || restaurantCart.items.length === 0) return;
    
    // Require logged-in user; do not allow guest orders
    if (!user) {
      showError(t('cart.orderRequiresAccount') || 'Orders cannot be placed without an account. Please log in or register to continue.');
      return;
    }
    
    // Check if restaurant/store is open or if preorder is set
    const restaurantInfo = restaurantData[restaurantCart.restaurantId];
    const isOpen = restaurantInfo ? isCurrentlyOpen(restaurantInfo) : true;
    const scheduledTime = scheduledTimes[restaurantCart.restaurantId];
    
    if (!isOpen && !scheduledTime) {
      showError(t('cart.mustSelectPreorderTime') || 'This restaurant/store is currently closed. Please select a preorder time to continue.');
      setShowPreorderPicker(prev => ({ ...prev, [restaurantCart.restaurantId]: true }));
      return;
    }
    
    // Check if out of delivery zone
    if (currentAddress?.coordinates?.lat && 
        currentAddress?.coordinates?.lng &&
        restaurantInfo && 
        restaurantInfo.distance !== undefined && 
        restaurantInfo.distance !== null &&
        restaurantInfo.maxDeliveryDistance !== undefined && 
        restaurantInfo.maxDeliveryDistance !== null &&
        restaurantInfo.distance > restaurantInfo.maxDeliveryDistance &&
        deliveryMethod === 'delivery') {
      showError(`You are ${restaurantInfo.distance.toFixed(1)} km away, but ${restaurantCart.restaurantName} only delivers up to ${restaurantInfo.maxDeliveryDistance} km. Please change delivery method or select a different address.`);
      return;
    }
    
    // Prevent duplicate orders with the same payment intent
    if (paymentIntentId && processedPaymentIntents.has(paymentIntentId)) {
      console.warn('Payment intent already processed, ignoring duplicate order');
      return;
    }
    
    // Mark payment intent as processed
    if (paymentIntentId) {
      setProcessedPaymentIntents(prev => new Set(prev).add(paymentIntentId));
    }
    
    setProcessingOrder(restaurantCart.restaurantId);
    try {
      // Use current delivery address from context, or fallback to saved addresses or current location
      const deliveryAddress = currentAddress || user?.savedAddresses?.[0] || {
        label: 'Current Location',
        street: '',
        city: '',
        zipCode: '',
        addressDetails: '',
        coordinates: { lat: 0, lng: 0 }
      };

      // Calculate subtotal including variation prices
      const calculateItemTotal = (item) => {
        let basePrice = item.price || 0;
        if (item.variations && item.selectedVariations) {
          item.variations.forEach(variation => {
            const selected = item.selectedVariations[variation.name];
            if (selected) {
              if (Array.isArray(selected)) {
                selected.forEach(optionName => {
                  const option = variation.options.find(opt => opt.name === optionName);
                  if (option) basePrice += option.price || 0;
                });
              } else {
                const option = variation.options.find(opt => opt.name === selected);
                if (option) basePrice += option.price || 0;
              }
            }
          });
        }
        return basePrice;
      };

      const subtotalInclTax = restaurantCart.items.reduce((sum, item) => sum + (calculateItemTotal(item) * item.quantity), 0);
      const isStore = restaurantCart.type === 'store';
      const restaurantInfo = restaurantData[restaurantCart.restaurantId];
      const deliveryFee = deliveryMethod === 'delivery' 
        ? (restaurantInfo?.deliveryFee ?? restaurantCart.deliveryFee ?? 0) 
        : 0;
      const serviceFeeRate = 0;
      const serviceFee = (subtotalInclTax + deliveryFee) * (serviceFeeRate / 100);
      const minOrder = Number(restaurantInfo?.minOrderPrice ?? 0) || 0;
      const minimumOrderFee = (minOrder > 0 && subtotalInclTax < minOrder) ? minOrder - subtotalInclTax : 0;
      const restaurantTotal = subtotalInclTax + minimumOrderFee + deliveryFee + serviceFee;

      // Calculate wallet amount to use (when user chose Wallet we use wallet; when they chose Card we only use wallet for partial, not when wallet would cover full)
      const discountAmount = (appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId) ? (appliedDiscount.discountAmount || 0) : 0;
      const finalAmount = restaurantTotal - discountAmount;
      const walletToUse = paymentMethod === 'wallet' && wallet
        ? Math.min(wallet.balance, finalAmount)
        : (paymentMethod === 'stripe' && useWallet && wallet && wallet.balance < finalAmount ? Math.min(wallet.balance, finalAmount) : 0);
      const remainingAmount = finalAmount - walletToUse;

      const scheduledTime = scheduledTimes[restaurantCart.restaurantId];
      const isPreorder = scheduledTime && new Date(scheduledTime) > new Date();

      const response = await axios.post(`${API_URL}/orders`, {
        restaurantId: restaurantCart.restaurantId,
        items: restaurantCart.items,
        deliveryAddress,
        deliveryMethod,
        deliveryFee: deliveryFee,
        type: isStore ? 'store' : 'restaurant',
        paymentIntentId: remainingAmount > 0 ? paymentIntentId : null,
        paymentMethod: remainingAmount === 0 ? 'wallet' : (walletToUse > 0 ? 'wallet' : (paymentMethod === 'cash' ? 'stripe' : paymentMethod)),
        walletAmount: walletToUse,
        discountCodeId: appliedDiscount?._id || null,
        restaurantInstructions: restaurantInstructions.trim(),
        driverInstructions: driverInstructions.trim(),
        scheduledTime: scheduledTime || null,
        isPreorder: isPreorder || false,
        customerName: customerDetails.name,
        customerPhone: customerDetails.phone,
        customerEmail: customerDetails.email
      });

      // Remove this restaurant's items from cart after successful order
      removeRestaurantCart(restaurantCart.restaurantId);
      
      // Refresh wallet balance if wallet was used
      if (walletToUse > 0) {
        fetchWallet();
      }
      
      // Clear discount code and instructions
      setAppliedDiscount(null);
      setDiscountCode('');
      setRestaurantInstructions('');
      setDriverInstructions('');
      // Clear scheduled time for this restaurant
      setScheduledTimes(prev => {
        const newTimes = { ...prev };
        delete newTimes[restaurantCart.restaurantId];
        return newTimes;
      });
      
      // Show success notification
      showSuccess(
        t('notifications.orderCreated') || `Order from ${restaurantCart.restaurantName} has been created successfully.`
      );
      
      // If cart is now empty, show message; otherwise stay on cart page
      if (cart.length === 1) {
        navigate('/orders');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      const msg = error.response?.data?.code === 'VENUE_CLOSED'
        ? t('restaurant.orderingPausedDueToCongestion')
        : (error.response?.data?.message || 'Failed to place order');
      showError(msg);
    } finally {
      setProcessingOrder(null);
      setShowPaymentForm(prev => ({ ...prev, [restaurantCart.restaurantId]: false }));
    }
  };

  const handlePaymentSuccess = (paymentIntent, restaurantCart) => {
    // Prevent duplicate order placement (ref checked synchronously so double callback = one order)
    if (processedPaymentIntentsRef.current.has(paymentIntent.id)) {
      console.warn('Payment intent already used for an order');
      return;
    }
    processedPaymentIntentsRef.current.add(paymentIntent.id);
    handlePlaceOrder(restaurantCart, paymentIntent.id);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    showError(t('payment.error') || `Payment failed: ${error}`);
  };

  const handleCheckoutClick = (restaurantCart) => {
    // Require logged-in user; do not allow guest orders
    if (!user) {
      showError(t('cart.orderRequiresAccount') || 'Orders cannot be placed without an account. Please log in or register to continue.');
      return;
    }
    // Validate cash payment limit
    if (paymentMethod === 'cash') {
      const calculateItemTotal = (item) => {
        let basePrice = item.price || 0;
        if (item.variations && item.selectedVariations) {
          item.variations.forEach(variation => {
            const selected = item.selectedVariations[variation.name];
            if (selected) {
              if (Array.isArray(selected)) {
                selected.forEach(optionName => {
                  const option = variation.options.find(opt => opt.name === optionName);
                  if (option) basePrice += option.price || 0;
                });
              } else {
                const option = variation.options.find(opt => opt.name === selected);
                if (option) basePrice += option.price || 0;
              }
            }
          });
        }
        return basePrice;
      };
      const subtotalInclTax = restaurantCart.items.reduce((sum, item) => sum + (calculateItemTotal(item) * item.quantity), 0);
      const restaurantInfo = restaurantData[restaurantCart.restaurantId];
      const deliveryFee = deliveryMethod === 'delivery' 
        ? (restaurantInfo?.deliveryFee ?? restaurantCart.deliveryFee ?? 0) 
        : 0;
      const serviceFeeRate = 0;
      const serviceFee = (subtotalInclTax + deliveryFee) * (serviceFeeRate / 100);
      const minOrder = Number(restaurantInfo?.minOrderPrice ?? 0) || 0;
      const minimumOrderFee = (minOrder > 0 && subtotalInclTax < minOrder) ? minOrder - subtotalInclTax : 0;
      const restaurantTotal = subtotalInclTax + minimumOrderFee + deliveryFee + serviceFee;
      const discountAmount = (appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId) ? (appliedDiscount.discountAmount || 0) : 0;
      const finalAmount = restaurantTotal - discountAmount;
      const walletToUse = useWallet && wallet ? Math.min(wallet.balance, finalAmount) : 0;
      const remainingAmount = finalAmount - walletToUse;
      const cashLimit = 100;
      const amountForCashCheck = subtotalInclTax + minimumOrderFee + deliveryFee - discountAmount - walletToUse;
      
      if (amountForCashCheck > cashLimit) {
        showError(t('cart.cashLimitExceeded', { limit: cashLimit.toFixed(2) }) || `Cash payment is only available for orders up to €${cashLimit.toFixed(2)}. Please use card payment for this order.`);
        return;
      }
    }
    
    if (paymentMethod === 'stripe') {
      // Show payment form
      setShowPaymentForm(prev => ({ ...prev, [restaurantCart.restaurantId]: true }));
    } else {
      // Place order without payment (cash on delivery, etc.)
      handlePlaceOrder(restaurantCart);
    }
  };

  // Cart display total: same formula as each restaurant block (subtotal with variations + delivery + service - discount, and - wallet when single restaurant)
  const getCartDisplayTotal = useMemo(() => {
    const calculateItemTotal = (item) => {
      let basePrice = item.price || 0;
      if (item.variations && item.selectedVariations) {
        item.variations.forEach((variation) => {
          const selected = item.selectedVariations[variation.name];
          if (selected) {
            if (Array.isArray(selected)) {
              selected.forEach((optionName) => {
                const option = variation.options?.find((o) => o.name === optionName);
                if (option) basePrice += option.price || 0;
              });
            } else {
              const option = variation.options?.find((o) => o.name === selected);
              if (option) basePrice += option.price || 0;
            }
          }
        });
      }
      return basePrice * item.quantity;
    };

    let total = 0;
    cart.forEach((restaurantCart) => {
      const subtotalInclTax = restaurantCart.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
      const restaurantInfo = restaurantData[restaurantCart.restaurantId];
      const deliveryFee = deliveryMethod === 'delivery'
        ? (restaurantInfo?.deliveryFee ?? restaurantCart.deliveryFee ?? 0)
        : 0;
      const serviceFeeRate = 0;
      const serviceFee = (subtotalInclTax + deliveryFee) * (serviceFeeRate / 100);
      const minOrder = Number(restaurantInfo?.minOrderPrice ?? 0) || 0;
      const minimumOrderFee = (minOrder > 0 && subtotalInclTax < minOrder) ? minOrder - subtotalInclTax : 0;
      const restaurantTotal = subtotalInclTax + minimumOrderFee + deliveryFee + serviceFee;
      const discountAmount = (appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId)
        ? (appliedDiscount.discountAmount || 0) : 0;
      total += restaurantTotal - discountAmount;
    });

    if (cart.length === 1 && useWallet && wallet && wallet.balance > 0) {
      total = Math.max(0, total - Math.min(wallet.balance, total));
    }
    return total;
  }, [cart, restaurantData, deliveryMethod, appliedDiscount, useWallet, wallet]);

  if (cart.length === 0) {
    return (
      <div className="cart-container">
        <div className="container">
          <h1>{t('cart.title')}</h1>
          <div className="empty-cart">{t('cart.empty')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <div className="container">
        <h1>{t('cart.title')}</h1>
        {!user && (
          <div className="cart-login-required-banner" role="alert">
            {t('cart.orderRequiresAccount')}
            <button type="button" className="btn btn-primary cart-login-link" onClick={() => navigate('/login', { state: { from: '/cart' } })}>
              {t('nav.login')}
            </button>
          </div>
        )}
        <div className="cart-items">
          {cart.map((restaurantCart) => {
            // Calculate subtotal including variation prices
            const calculateItemTotal = (item) => {
              let basePrice = item.price || 0;
              if (item.variations && item.selectedVariations) {
                item.variations.forEach(variation => {
                  const selected = item.selectedVariations[variation.name];
                  if (selected) {
                    if (Array.isArray(selected)) {
                      selected.forEach(optionName => {
                        const option = variation.options?.find(o => o.name === optionName);
                        if (option) basePrice += option.price || 0;
                      });
                    } else {
                      const option = variation.options?.find(o => o.name === selected);
                      if (option) basePrice += option.price || 0;
                    }
                  }
                });
              }
              return basePrice * item.quantity;
            };
            
            // Product prices are tax-inclusive; this sum is the total for items (incl. tax)
            const subtotalInclTax = restaurantCart.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
            const taxRate = restaurantCart.type === 'store' ? 0.255 : 0.135;
            // Tax portion embedded in the product total: taxInclTotal * rate / (1 + rate)
            const taxAmountFromProducts = subtotalInclTax * (taxRate / (1 + taxRate));
            const restaurantInfo = restaurantData[restaurantCart.restaurantId];
            const deliveryFee = deliveryMethod === 'delivery' 
              ? (restaurantInfo?.deliveryFee ?? restaurantCart.deliveryFee ?? 0) 
              : 0;
            const serviceFeeRate = 0;
            const serviceFee = (subtotalInclTax + deliveryFee) * (serviceFeeRate / 100);
            const isOpen = restaurantInfo ? isCurrentlyOpen(restaurantInfo) : true;
            const scheduledTime = scheduledTimes[restaurantCart.restaurantId];
            const isPreorder = scheduledTime && new Date(scheduledTime) > new Date();
            const isOutOfDeliveryZone = currentAddress?.coordinates?.lat && 
              currentAddress?.coordinates?.lng &&
              restaurantInfo && 
              restaurantInfo.distance !== undefined && 
              restaurantInfo.distance !== null &&
              restaurantInfo.maxDeliveryDistance !== undefined && 
              restaurantInfo.maxDeliveryDistance !== null &&
              restaurantInfo.distance > restaurantInfo.maxDeliveryDistance &&
              deliveryMethod === 'delivery';

            const minOrder = Number(restaurantInfo?.minOrderPrice ?? 0) || 0;
            const belowMinimumOrder = minOrder > 0 && subtotalInclTax < minOrder;
            const minimumOrderFee = belowMinimumOrder ? minOrder - subtotalInclTax : 0;
            const restaurantTotal = subtotalInclTax + minimumOrderFee + deliveryFee + serviceFee;

            const trackStock = restaurantInfo?.settings?.trackStock === true;
            const itemsList = trackStock && restaurantInfo
              ? (restaurantCart.type === 'store' ? restaurantInfo.products : restaurantInfo.menu) || []
              : [];
            const outOfStockItems = trackStock ? restaurantCart.items.map(item => {
              const sub = itemsList.find(p => String(p._id) === String(item._id));
              const available = sub?.stockQty != null ? Number(sub.stockQty) : 0;
              const requested = item.quantity || 0;
              return requested > available ? { name: item.name, requested, available } : null;
            }).filter(Boolean) : [];
            const hasOutOfStock = outOfStockItems.length > 0;

            return (
            <div key={restaurantCart.restaurantId} className="restaurant-cart">
              <h2>{restaurantCart.restaurantName}</h2>
              
              {/* Preorder Notice */}
              {!isOpen && !scheduledTime && (
                <div className="preorder-notice">
                  <i className="fas fa-clock"></i>
                  <div className="notice-content">
                    <strong>{t('cart.closed') || 'Currently Closed'}</strong>
                    <p>{t('cart.preorderAvailable') || 'This restaurant/store is currently closed. You can place a preorder for when it opens again.'}</p>
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowPreorderPicker(prev => ({ ...prev, [restaurantCart.restaurantId]: true }))}
                    >
                      {t('cart.selectPreorderTime') || 'Select Preorder Time'}
                    </button>
                  </div>
                </div>
              )}

              {/* Scheduled Time Display */}
              {scheduledTime && (
                <div className="scheduled-time-display">
                  <i className="fas fa-calendar-check"></i>
                  <div className="scheduled-content">
                    <strong>{t('cart.scheduledFor') || 'Scheduled for'}:</strong>
                    <span>
                      {new Date(scheduledTime).toLocaleString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <button 
                      className="btn-link"
                      onClick={() => {
                        setScheduledTimes(prev => {
                          const newTimes = { ...prev };
                          delete newTimes[restaurantCart.restaurantId];
                          return newTimes;
                        });
                      }}
                    >
                      {t('cart.change') || 'Change'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Delivery Zone Warning */}
              {isOutOfDeliveryZone && (
                <div className="delivery-zone-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <div className="warning-content">
                    <strong>Out of Delivery Zone</strong>
                    <p>You are {restaurantInfo.distance.toFixed(1)} km away, but {restaurantCart.restaurantName} only delivers up to {restaurantInfo.maxDeliveryDistance} km. Orders cannot be placed for delivery.</p>
                  </div>
                </div>
              )}

              {/* Minimum order not met - user can pay minimum order fee to complete */}
              {belowMinimumOrder && (
                <div className="cart-minimum-order-warning">
                  <i className="fas fa-shopping-cart"></i>
                  <div className="warning-content">
                    <strong>{t('cart.belowMinimumOrder')}</strong>
                    <p>{t('cart.minimumOrderValue', { value: minOrder.toFixed(2) })}</p>
                    <p>{t('cart.belowMinimumCanPayFee', { fee: minimumOrderFee.toFixed(2) })}</p>
                  </div>
                </div>
              )}

              {/* Out of stock warning when venue tracks stock */}
              {hasOutOfStock && (
                <div className="cart-out-of-stock-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <div className="warning-content">
                    <strong>{t('cart.outOfStockTitle') || 'Some items are out of stock'}</strong>
                    <p>{t('cart.outOfStockMessage') || 'The following items have insufficient stock. Please remove them or reduce quantity to place your order.'}</p>
                    <ul>
                      {outOfStockItems.map((o, i) => (
                        <li key={i}>{o.name} — {t('cart.requested') || 'requested'}: {o.requested}, {t('cart.available') || 'available'}: {o.available}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {restaurantCart.items.map((item, itemIndex) => {
                // Generate unique key for items with variations
                // Use itemIndex to ensure uniqueness even if items have same ID/variations
                const variationsKey = item.selectedVariations && Object.keys(item.selectedVariations).length > 0
                  ? JSON.stringify(item.selectedVariations)
                  : 'no-variations';
                const itemKey = `${restaurantCart.restaurantId}-${item._id}-${variationsKey}-${itemIndex}`;
                
                // Calculate price including variations
                const calculateItemPrice = () => {
                  let basePrice = item.price || 0;
                  if (item.variations && item.selectedVariations) {
                    item.variations.forEach(variation => {
                      const selected = item.selectedVariations[variation.name];
                      if (selected) {
                        if (Array.isArray(selected)) {
                          selected.forEach(optionName => {
                            const option = variation.options?.find(o => o.name === optionName);
                            if (option) basePrice += option.price || 0;
                          });
                        } else {
                          const option = variation.options?.find(o => o.name === selected);
                          if (option) basePrice += option.price || 0;
                        }
                      }
                    });
                  }
                  return basePrice;
                };
                
                const itemPriceWithVariations = calculateItemPrice();
                
                return (
                <div key={itemKey} className="cart-item">
                  <div className="cart-item-info">
                    <h3>{item.name}</h3>
                    {item.description && <p className="item-description">{item.description}</p>}
                    <div className="cart-item-variations">
                      {item.ingredients && item.ingredients.length > 0 && (
                        <div className="cart-item-ingredients">
                          {item.selectedIngredients && item.selectedIngredients.length > 0 ? (
                            <span className="ingredients-label">
                              {t('cart.ingredientsIncluded') || 'Ingredients'}: {item.selectedIngredients.join(', ')}
                            </span>
                          ) : (
                            <span className="ingredients-label">{t('cart.noIngredients') || 'No ingredients selected'}</span>
                          )}
                          {item.selectedIngredients && item.ingredients.some(ing => !item.selectedIngredients.includes(ing)) && (
                            <span className="ingredients-excluded">
                              ({t('cart.without') || 'Without'}: {item.ingredients.filter(ing => !item.selectedIngredients.includes(ing)).join(', ')})
                            </span>
                          )}
                        </div>
                      )}
                      {item.selectedVariations && Object.keys(item.selectedVariations).length > 0 ? (
                        <>
                          {Object.entries(item.selectedVariations).map(([variationName, selectedOptions]) => {
                            const optionsArray = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions];
                            return (
                              <div key={variationName} className="variation-group">
                                <span className="variation-name-label">{variationName}:</span>
                                <div className="variation-options-container">
                                  {optionsArray.map((option, optIndex) => (
                                    <span key={optIndex} className="variation-option-badge">
                                      {option}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          <button
                            onClick={() => {
                              setEditingItem({
                                item: item,
                                restaurantId: restaurantCart.restaurantId,
                                restaurantName: restaurantCart.restaurantName,
                                type: restaurantCart.type
                              });
                              setShowEditModal(true);
                            }}
                            className="btn-edit-variations"
                            title={t('cart.editVariations') || 'Edit choices'}
                          >
                            <i className="fas fa-edit"></i> {t('cart.editChoices') || 'Edit Choices'}
                          </button>
                        </>
                      ) : (
                        ((item.variations && item.variations.length > 0) || (item.ingredients && item.ingredients.length > 0)) && (
                          <button
                            onClick={() => {
                              setEditingItem({
                                item: item,
                                restaurantId: restaurantCart.restaurantId,
                                restaurantName: restaurantCart.restaurantName,
                                type: restaurantCart.type
                              });
                              setShowEditModal(true);
                            }}
                            className="btn-edit-variations"
                            title={item.variations?.length && !item.selectedVariations ? (t('cart.addVariations') || 'Add choices') : (t('cart.editVariations') || 'Edit choices')}
                          >
                            <i className="fas fa-edit"></i> {t('cart.editChoices') || 'Edit Choices'}
                          </button>
                        )
                      )}
                    </div>
                    <span className="item-price">€{itemPriceWithVariations.toFixed(2)}</span>
                  </div>
                  <div className="cart-item-controls">
                    <div className="quantity-controls">
                      <button
                        onClick={() => handleUpdateQuantity(restaurantCart.restaurantId, item, item.quantity - 1)}
                        className="quantity-btn"
                      >
                        -
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button
                        onClick={() => handleUpdateQuantity(restaurantCart.restaurantId, item, item.quantity + 1)}
                        className="quantity-btn"
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(restaurantCart.restaurantId, item)}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('cart.remove')}
                    </button>
                    <div className="item-total">
                      €{(itemPriceWithVariations * item.quantity).toFixed(2)}
                    </div>
                  </div>
                </div>
              );
              })}
              <div className="restaurant-cart-summary">
                <div className="cart-summary-row">
                  <span>{t('cart.itemsInclTax') || 'Items (incl. tax)'}:</span>
                  <span>€{subtotalInclTax.toFixed(2)}</span>
                </div>
                <div className="cart-summary-row">
                  <span>{t('cart.taxIncluded') || 'Tax (included)'}:</span>
                  <span>€{taxAmountFromProducts.toFixed(2)}</span>
                </div>
                {minimumOrderFee > 0 && (
                  <div className="cart-summary-row">
                    <span>{t('cart.minimumOrderFee') || 'Minimum order fee'}:</span>
                    <span>€{minimumOrderFee.toFixed(2)}</span>
                  </div>
                )}
                {deliveryMethod === 'delivery' && (
                  <div className="cart-summary-row">
                    <span>{t('cart.deliveryFee') || 'Delivery Fee'}:</span>
                    <span>€{deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                {serviceFee > 0 && (
                  <div className="cart-summary-row">
                    <span>{t('cart.serviceFee') || 'Service Fee'} ({serviceFeeRate.toFixed(2)}%):</span>
                    <span>€{serviceFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="cart-summary-row cart-subtotal-row">
                  <span>{t('cart.subtotal') || 'Subtotal'}:</span>
                  <span>€{restaurantTotal.toFixed(2)}</span>
                </div>
                {appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId && (
                  <div className="cart-summary-row discount-row">
                    <span>
                      {t('cart.discount') || 'Discount'} ({appliedDiscount.code}):
                    </span>
                    <span className="discount-amount">
                      -€{appliedDiscount.discountAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                {useWallet && wallet && wallet.balance > 0 && (
                  <div className="cart-summary-row wallet-row">
                    <span>{t('cart.walletPayment') || 'Wallet Payment'}:</span>
                    <span className="wallet-amount">
                      -€{Math.min(wallet.balance, restaurantTotal - ((appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId) ? appliedDiscount.discountAmount : 0)).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="restaurant-total">
                  <strong>
                    {t('cart.total') || 'Total'}: €{
                      Math.max(0, restaurantTotal - ((appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId) ? appliedDiscount.discountAmount : 0) - (useWallet && wallet ? Math.min(wallet.balance, restaurantTotal - ((appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId) ? appliedDiscount.discountAmount : 0)) : 0)).toFixed(2)
                    }
                  </strong>
                </div>
                {deliveryMethod === 'delivery' && (
                  <div className="cart-delivery-address">
                    <label>{t('cart.deliveryAddress') || 'Delivery Address'}:</label>
                    <div className="delivery-address-edit">
                      <input
                        type="text"
                        value={currentAddress?.street || ''}
                        onChange={(e) => {
                          setCurrentAddress({
                            ...(currentAddress || {}),
                            street: e.target.value,
                            city: currentAddress?.city || '',
                            zipCode: currentAddress?.zipCode || '',
                            addressDetails: currentAddress?.addressDetails || '',
                            coordinates: currentAddress?.coordinates || { lat: 0, lng: 0 }
                          });
                        }}
                        placeholder={t('cart.enterAddress') || 'Enter delivery address'}
                        className="address-input"
                      />
                      <input
                        type="text"
                        value={currentAddress?.city || ''}
                        onChange={(e) => {
                          setCurrentAddress({
                            ...(currentAddress || {}),
                            street: currentAddress?.street || '',
                            city: e.target.value,
                            zipCode: currentAddress?.zipCode || '',
                            addressDetails: currentAddress?.addressDetails || '',
                            coordinates: currentAddress?.coordinates || { lat: 0, lng: 0 }
                          });
                        }}
                        placeholder={t('cart.city') || 'City'}
                        className="address-input"
                      />
                      <input
                        type="text"
                        value={currentAddress?.zipCode || ''}
                        onChange={(e) => {
                          setCurrentAddress({
                            ...(currentAddress || {}),
                            street: currentAddress?.street || '',
                            city: currentAddress?.city || '',
                            zipCode: e.target.value,
                            addressDetails: currentAddress?.addressDetails || '',
                            coordinates: currentAddress?.coordinates || { lat: 0, lng: 0 }
                          });
                        }}
                        placeholder={t('cart.zipCode') || 'Zip Code'}
                        className="address-input"
                      />
                      <input
                        type="text"
                        value={currentAddress?.addressDetails || ''}
                        onChange={(e) => {
                          setCurrentAddress({
                            ...(currentAddress || {}),
                            street: currentAddress?.street || '',
                            city: currentAddress?.city || '',
                            zipCode: currentAddress?.zipCode || '',
                            addressDetails: e.target.value,
                            coordinates: currentAddress?.coordinates || { lat: 0, lng: 0 }
                          });
                        }}
                        placeholder={t('cart.addressDetailsPlaceholder') || 'Staircase, apartment number (optional)'}
                        className="address-input"
                      />
                    </div>
                  </div>
                )}
                <div className="cart-delivery-method">
                  <label>{t('cart.deliveryMethod') || 'Delivery Method'}:</label>
                  <select value={deliveryMethod} onChange={(e) => setDeliveryMethod(e.target.value)}>
                    <option value="delivery">{t('cart.delivery') || 'Delivery'}</option>
                    <option value="pickup">{t('cart.pickup') || 'Pickup'}</option>
                    <option value="eatIn">{t('cart.eatIn') || 'Eat In'}</option>
                  </select>
                </div>
                <div className="cart-customer-details">
                  <h3>{t('cart.customerDetails') || 'Customer Details'}</h3>
                  <div className="form-group">
                    <label>{t('cart.customerName') || 'Full Name'}:</label>
                    <input
                      type="text"
                      value={customerDetails.name}
                      onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                      placeholder={t('cart.enterName') || 'Enter your full name'}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('cart.customerPhone') || 'Phone Number'}:</label>
                    <input
                      type="tel"
                      value={customerDetails.phone}
                      onChange={(e) => setCustomerDetails({ ...customerDetails, phone: e.target.value })}
                      placeholder={t('cart.enterPhone') || 'Enter your phone number'}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('cart.customerEmail') || 'Email'}:</label>
                    <input
                      type="email"
                      value={customerDetails.email}
                      onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                      placeholder={t('cart.enterEmail') || 'Enter your email'}
                      required
                    />
                  </div>
                </div>
                <div className="cart-customer-instructions">
                  <label>
                    <i className="fas fa-utensils"></i>
                    {t('cart.restaurantInstructions') || 'Instructions for restaurant'}
                  </label>
                  <p className="instructions-help-text">
                    {t('cart.restaurantInstructionsHelp') || 'E.g. cooking preferences, allergies, extra sauce'}
                  </p>
                  <textarea
                    value={restaurantInstructions}
                    onChange={(e) => setRestaurantInstructions(e.target.value)}
                    placeholder={t('cart.restaurantInstructionsPlaceholder') || 'Instructions for the restaurant...'}
                    rows={2}
                    maxLength={500}
                    className="customer-instructions-input"
                  />
                  <div className="instructions-char-count">
                    {restaurantInstructions.length}/500
                  </div>
                  <label className="instructions-label-driver">
                    <i className="fas fa-truck"></i>
                    {t('cart.driverInstructions') || 'Instructions for delivery driver'}
                  </label>
                  <p className="instructions-help-text">
                    {t('cart.driverInstructionsHelp') || 'E.g. "Ring doorbell", "Leave at door", "Possible door code"'}
                  </p>
                  <textarea
                    value={driverInstructions}
                    onChange={(e) => setDriverInstructions(e.target.value)}
                    placeholder={t('cart.driverInstructionsPlaceholder') || 'Instructions for the driver...'}
                    rows={2}
                    maxLength={500}
                    className="customer-instructions-input"
                  />
                  <div className="instructions-char-count">
                    {driverInstructions.length}/500
                  </div>
                </div>
                {wallet && wallet.balance > 0 && (
                  <div className="cart-wallet-option">
                    <label>
                      <input
                        type="checkbox"
                        checked={useWallet}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseWallet(checked);
                          setPaymentMethod(checked ? 'wallet' : 'stripe');
                          setShowPaymentForm(prev => ({ ...prev, [restaurantCart.restaurantId]: false }));
                        }}
                      />
                      <span>
                        {t('cart.useWallet') || 'Use Wallet'} ({t('cart.balance') || 'Balance'}: €{wallet.balance.toFixed(2)})
                      </span>
                    </label>
                  </div>
                )}
                <div className="cart-discount-code">
                  <label>{t('cart.discountCode') || 'Discount Code'}:</label>
                  {appliedDiscount && appliedDiscount.restaurantId !== restaurantCart.restaurantId ? (
                    <p className="discount-one-only-msg">
                      {t('cart.onlyOneDiscountActive') || 'A discount code is already applied to another order. Only one discount can be active at a time.'}
                    </p>
                  ) : (
                    <>
                      <div className="discount-code-input">
                        <input
                          type="text"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          placeholder={t('cart.enterDiscountCode') || 'Enter discount code'}
                          onBlur={() => handleApplyDiscountCode(restaurantCart)}
                        />
                        <button
                          type="button"
                          onClick={() => handleApplyDiscountCode(restaurantCart)}
                          className="btn-apply-discount"
                        >
                          {t('cart.apply') || 'Apply'}
                        </button>
                      </div>
                      {appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId && (
                        <div className="discount-applied">
                          <i className="fas fa-check-circle"></i>
                          <span>{t('cart.discountApplied') || 'Discount applied'}</span>
                          <button
                            type="button"
                            onClick={() => { setAppliedDiscount(null); setDiscountCode(''); }}
                            className="btn-remove-discount"
                            aria-label={t('cart.removeDiscount') || 'Remove discount'}
                          >
                            {t('cart.removeDiscount') || 'Remove'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="cart-payment-method">
                  <label>{t('cart.paymentMethod') || 'Payment Method'}:</label>
                  <select value={paymentMethod === 'cash' ? 'stripe' : paymentMethod} onChange={(e) => {
                    const value = e.target.value;
                    setPaymentMethod(value);
                    setUseWallet(value === 'wallet');
                    setShowPaymentForm(prev => ({ ...prev, [restaurantCart.restaurantId]: false }));
                  }}>
                    {wallet && wallet.balance > 0 && (
                      <option value="wallet">{t('cart.useWallet') || 'Use Wallet'} (€{wallet.balance.toFixed(2)})</option>
                    )}
                    <option value="stripe">{t('payment.stripe') || 'Card Payment'}</option>
                  </select>
                </div>
                {showPaymentForm[restaurantCart.restaurantId] && paymentMethod === 'stripe' ? (
                  <div className="payment-section">
                    <StripePayment
                      amount={Math.max(0, (() => {
                        const disc = (appliedDiscount && appliedDiscount.restaurantId === restaurantCart.restaurantId) ? appliedDiscount.discountAmount : 0;
                        const totalAfterDiscount = restaurantTotal - disc;
                        const walletDeduction = (useWallet && wallet && wallet.balance < totalAfterDiscount) ? Math.min(wallet.balance, totalAfterDiscount) : 0;
                        return totalAfterDiscount - walletDeduction;
                      })())}
                      onSuccess={(paymentIntent) => handlePaymentSuccess(paymentIntent, restaurantCart)}
                      onError={handlePaymentError}
                      restaurantName={restaurantCart.restaurantName}
                      restaurantId={restaurantCart.restaurantId}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => handleCheckoutClick(restaurantCart)}
                    className="btn btn-primary"
                    disabled={!user || processingOrder === restaurantCart.restaurantId || isOutOfDeliveryZone || hasOutOfStock}
                  >
                    {processingOrder === restaurantCart.restaurantId 
                      ? (t('cart.placingOrder') || 'Placing Order...') 
                      : t('cart.checkout')}
                  </button>
                )}
              </div>

              {/* Preorder Time Picker Modal */}
              {showPreorderPicker[restaurantCart.restaurantId] && restaurantInfo && (
                <PreorderTimePicker
                  restaurantOrStore={restaurantInfo}
                  value={scheduledTime}
                  onChange={(dateTime) => {
                    setScheduledTimes(prev => ({
                      ...prev,
                      [restaurantCart.restaurantId]: dateTime.toISOString()
                    }));
                    setShowPreorderPicker(prev => ({ ...prev, [restaurantCart.restaurantId]: false }));
                  }}
                  onClose={() => setShowPreorderPicker(prev => ({ ...prev, [restaurantCart.restaurantId]: false }))}
                />
              )}
            </div>
            );
          })}
        </div>
        {cart.length > 0 && (
          <div className="cart-summary">
            <div className="cart-total">
              <strong>{t('cart.total')}: €{getCartDisplayTotal.toFixed(2)}</strong>
            </div>
            <p className="cart-note">{t('cart.checkoutNote') || 'Note: You can only checkout one restaurant/store at a time. Select a restaurant/store above to place an order.'}</p>
          </div>
        )}
      </div>
      {showEditModal && editingItem && (
        <ItemDetailModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          item={editingItem.item}
          restaurantId={editingItem.restaurantId}
          restaurantName={editingItem.restaurantName}
          isProduct={editingItem.type === 'store'}
          isEditing={true}
          existingVariations={editingItem.item.selectedVariations}
          existingIngredients={editingItem.item.selectedIngredients}
          onSaveVariations={(newVariations, newIngredients) => {
            updateItemVariations(
              editingItem.restaurantId,
              editingItem.item._id,
              editingItem.item.selectedVariations,
              newVariations,
              editingItem.item.selectedIngredients || null,
              newIngredients !== undefined ? newIngredients : editingItem.item.selectedIngredients
            );
            setShowEditModal(false);
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
};

export default Cart;
